import React, { useState, useEffect, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Users, Maximize, ArrowRight, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { roomsApi, roomTypesApi } from '../services/api';
import { rooms as mockRooms } from '../mockData';
import { useSEO } from '../hooks/useSEO';
import roomsImage from '../assets/images/rooms.webp';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';

const RoomsPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [currency, setCurrency] = useState('USD');
  const [sortBy, setSortBy] = useState('price-low');
  const [selectedType, setSelectedType] = useState(searchParams.get('type') || 'all');
  const [rooms, setRooms] = useState([]);
  const [roomTypes, setRoomTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useSEO({
    title: 'Rooms & Suites | OlivOak Villa',
    description: 'Browse luxury rooms and suites at OlivOak Villa, Harare. View photos, amenities, pricing, and book your perfect accommodation today.'
  });

  useEffect(() => {
    fetchRooms();
    fetchRoomTypes();
  }, []);

  const fetchRooms = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await roomsApi.getAll();
      
      // Transform API data to match frontend format
      const transformedRooms = data.map(room => ({
        id: room.slug || room.id,
        dbId: room.id,
        name: room.name,
        description: room.description,
        shortDescription: room.short_description,
        roomTypeId: room.room_type_id,
        roomTypeName: room.room_types?.name || null,
        occupancy: { max: room.max_occupancy, beds: room.beds },
        size: `${room.size_sqm} sqm`,
        price: {
          USD: room.price_usd,
          ZAR: room.price_zar,
          GBP: room.price_gbp,
          EUR: room.price_eur
        },
        images: room.images || [],
        amenities: room.amenities || [],
        available: room.available
      }));
      
      setRooms(transformedRooms.length > 0 ? transformedRooms : mockRooms);
    } catch (err) {
      console.error('Failed to fetch rooms:', err);
      setError('Unable to load rooms from server');
      // Fallback to mock data
      setRooms(mockRooms);
    } finally {
      setLoading(false);
    }
  };

  const fetchRoomTypes = async () => {
    try {
      const data = await roomTypesApi.getAll(true);
      setRoomTypes(data || []);
    } catch (err) {
      console.error('Failed to fetch room types:', err);
    }
  };

  const handleTypeChange = (value) => {
    setSelectedType(value);
    if (value === 'all') {
      searchParams.delete('type');
    } else {
      searchParams.set('type', value);
    }
    setSearchParams(searchParams, { replace: true });
  };

  const filteredRooms = useMemo(() => {
    let result = [...rooms];
    if (selectedType && selectedType !== 'all') {
      result = result.filter(r => r.roomTypeId === selectedType);
    }
    return result.sort((a, b) => {
      if (sortBy === 'price-low') return a.price[currency] - b.price[currency];
      if (sortBy === 'price-high') return b.price[currency] - a.price[currency];
      return 0;
    });
  }, [rooms, selectedType, sortBy, currency]);

  if (loading) {
    return (
      <div style={{ backgroundColor: 'var(--bg-primary)', minHeight: '100vh', paddingTop: '80px' }}>
        <div className="flex items-center justify-center py-32">
          <Loader2 className="animate-spin" size={40} style={{ color: 'var(--olive-primary)' }} />
        </div>
      </div>
    );
  }

  return (
    <div style={{ backgroundColor: 'var(--bg-primary)', minHeight: '100vh', paddingTop: '80px' }}>
      {/* Hero Banner */}
      <section
        className="relative py-24"
        style={{
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.4), rgba(0, 0, 0, 0.4)), url('${roomsImage}')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="container text-center">
          <h1 className="hero-large mb-4" style={{ color: '#ffffff' }}>
            Rooms & Suites
          </h1>
          <p className="body-large" style={{ color: '#f0f0f0', maxWidth: '600px', margin: '0 auto' }}>
            Choose from our collection of elegantly appointed accommodations
          </p>
        </div>
      </section>

      {/* Filters */}
      <section className="py-8" style={{ backgroundColor: 'var(--bg-secondary)', borderBottom: '1px solid var(--border-light)' }}>
        <div className="container">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="body-regular" style={{ color: 'var(--text-secondary)' }}>
              {filteredRooms.length} room{filteredRooms.length !== 1 ? 's' : ''} available
            </p>
            <div className="flex gap-4">
              {roomTypes.length > 0 && (
                <div>
                  <label className="body-small block mb-2" style={{ color: 'var(--text-secondary)' }}>
                    Room Type
                  </label>
                  <Select value={selectedType} onValueChange={handleTypeChange}>
                    <SelectTrigger className="w-[180px]" style={{ borderRadius: 0 }} data-testid="type-filter">
                      <SelectValue placeholder="All Types" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Types</SelectItem>
                      {roomTypes.map(rt => (
                        <SelectItem key={rt.id} value={rt.id}>{rt.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div>
                <label className="body-small block mb-2" style={{ color: 'var(--text-secondary)' }}>
                  Sort By
                </label>
                <Select value={sortBy} onValueChange={setSortBy}>
                  <SelectTrigger className="w-[180px]" style={{ borderRadius: 0 }} data-testid="sort-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="price-low">Price: Low to High</SelectItem>
                    <SelectItem value="price-high">Price: High to Low</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Rooms Grid */}
      <section className="section-padding">
        <div className="container">
          {error && (
            <div className="mb-8 p-4 text-center" style={{ backgroundColor: 'var(--bg-secondary)', border: '1px solid var(--border-light)' }}>
              <p className="body-small" style={{ color: 'var(--text-secondary)' }}>
                {error}. Showing sample rooms.
              </p>
            </div>
          )}
          {filteredRooms.length === 0 ? (
            <div className="text-center py-16">
              <p className="heading-4 mb-2" style={{ color: 'var(--text-secondary)' }}>No rooms found</p>
              <p className="body-small mb-6" style={{ color: 'var(--text-muted)' }}>
                Try selecting a different room type or clearing the filter.
              </p>
              {selectedType !== 'all' && (
                <Button
                  variant="outline"
                  onClick={() => handleTypeChange('all')}
                  style={{ borderRadius: 0, borderColor: 'var(--olive-primary)', color: 'var(--olive-primary)' }}
                >
                  View All Rooms
                </Button>
              )}
            </div>
          ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {filteredRooms.map((room) => (
              <div
                key={room.id}
                className="hover-lift"
                style={{
                  backgroundColor: 'var(--bg-primary)',
                  border: '1px solid var(--border-light)',
                  overflow: 'hidden'
                }}
                data-testid={`room-card-${room.id}`}
              >
                <img
                  src={room.images[0]}
                  alt={room.name}
                  className="w-full h-80 object-cover"
                />
                <div className="p-8">
                  <h2 className="heading-2 mb-4">{room.name}</h2>
                  <p className="body-regular mb-6" style={{ color: 'var(--text-secondary)' }}>
                    {room.description}
                  </p>

                  <div className="grid grid-cols-2 gap-4 mb-6">
                    <div className="flex items-center gap-2">
                      <Users size={18} style={{ color: 'var(--text-secondary)' }} />
                      <span className="body-small" style={{ color: 'var(--text-secondary)' }}>
                        Up to {room.occupancy.max} guests
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Maximize size={18} style={{ color: 'var(--text-secondary)' }} />
                      <span className="body-small" style={{ color: 'var(--text-secondary)' }}>
                        {room.size}
                      </span>
                    </div>
                  </div>

                  <div className="flex items-center justify-between pt-6" style={{ borderTop: '1px solid var(--border-light)' }}>
                    <div>
                      <p className="body-small mb-1" style={{ color: 'var(--text-secondary)' }}>
                        From
                      </p>
                      <p className="heading-3">
                        ${room.price[currency]}
                        <span className="body-small" style={{ color: 'var(--text-secondary)' }}> /night</span>
                      </p>
                    </div>
                    <Link to={`/rooms/${room.id}`}>
                      <Button className="btn-primary flex items-center gap-2" data-testid={`view-room-${room.id}`}>
                        View Details
                        <ArrowRight size={16} />
                      </Button>
                    </Link>
                  </div>
                </div>
              </div>
            ))}
          </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default RoomsPage;
